import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { VisitView } from '@lib/guarantees/data-access';
import { ChipComponent } from '@lib/guarantees/feature/components/common/chip.component';
import { StreamVisitComponent } from '@lib/guarantees/feature/components/stream-visit/stream-visit.component';
import { TranslatePipe } from '@ngx-translate/core';

import { MatTooltip } from '@angular/material/tooltip';

/**
 * The calendar day a timestamp falls on **locally**. Slicing the ISO string would take the UTC
 * day, which puts a late-evening visit under the previous date while the card beside it prints
 * the next one.
 */
function localDayKey(timestamp: string): string {
  const date = new Date(timestamp);

  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

/** Measured against a rendered card; only close enough to keep the scroll extent believable. */
const CARD_HEADER_HEIGHT = 66;
const GROUP_HEADER_HEIGHT = 26;
const LINE_HEIGHT = 34;

/** The vertical care stream: the member's visits in reverse chronological order. */
@Component({
  selector: 'guarantees-care-stream',
  standalone: true,
  imports: [DatePipe, MatTooltip, TranslatePipe, ChipComponent, StreamVisitComponent],
  templateUrl: './care-stream.component.html',
  /** On the host, not the content below it: a container query resolves against an ancestor, never itself. */
  host: { class: '@container/line block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CareStreamComponent {
  visits = input.required<VisitView[]>();

  /** Withholds every control that writes, for a portal that may read the record but not change it. */
  readOnly = input<boolean>(false);
  /** The visit a chain link points at, if it is inside the loaded window. */
  focusedGuaranteeId = input<string | null>(null);

  /** A card asking for the rest of its record, and a line asking for the rest of its own. */
  detailsOpened = output<string>();
  prescriptionDetailsOpened = output<string>();

  /** A case named on the care plan, asking to be read in the panel beside the stream. */
  caseOpened = output<string>();

  /**
   * Grouped by the calendar day the record was raised, so several guarantees written on one day
   * read as one block instead of a run of near-identical cards. The same fact the stream is ordered
   * by — a header dated otherwise would head a run of cards that did not belong under it.
   */
  protected readonly days = computed(() => {
    // The header renders a real timestamp, not the key, so it goes through the same pipe as the cards.
    const groups: { key: string; timestamp: string; visits: VisitView[] }[] = [];

    for (const visit of this.visits()) {
      const key = localDayKey(visit.createdAt);
      const last = groups.at(-1);
      if (last?.key === key) {
        last.visits.push(visit);
      } else {
        groups.push({ key, timestamp: visit.createdAt, visits: [visit] });
      }
    }

    return groups;
  });

  /**
   * What a card will occupy once it is built, from the counts the timeline already carries. It only
   * has to be close: the placeholder is replaced before it is read, and its job is to stop the
   * scrollbar from lying about how much history is below.
   */
  protected placeholderHeight(visit: VisitView): number {
    const groups = visit.diagnosisGroups.length || 1;

    return CARD_HEADER_HEIGHT + groups * GROUP_HEADER_HEIGHT + visit.prescriptionCount * LINE_HEIGHT;
  }
}
